// import css from '@emotion/css';
import css from "@emotion/css";
import styled from "@emotion/styled";
import { data } from "flickity";
import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useRecoilValue } from "recoil";
import baseKeyState from "../../../recoil/baseKeyState";
import { api } from "../../../utils/api";
import useWindowSize from "../../../utils/useWindowSize";
import Modal from "../../Modal";
import Paginate from "../../Paginate";
import keys, { FUTURE, regex } from "../keys";
import { isScoreBeta } from "../../../utils/beta";
import { enharmonic, fromRow, GUITAR, parseChord, pickDistinct, scoreVoicing, searchVoicings, tableName, toRow, UKULELE } from "./voicings";
import { track } from "../../../utils/track";
import simpleChordState from "../../../recoil/simpleChordState";
import { simplifyChord } from "../simpleChord";

/**
 * 코드 하나의 운지법 목록: DB(사람이 등록) + 계산한 운지법을 합쳐 '많이 쓰는 순'으로 (2026-10).
 * 1번 = 대표 운지법(개방 기본 코드·교본 표준 바레 모양). 코드에 없는 음이 섞인 잘못된 DB 운지법은 뺀다.
 */
const MAX_SHOW = { guitar: 8, ukelele: 6 } as const;
const buildVoicings = (rawName: string, type: "guitar" | "ukelele", allChordCode: any[]) => {
  const tn = tableName(rawName);
  const alt = enharmonic(tn);
  const dbRows = allChordCode.filter((x) => x.type == type && (x.name == tn || (alt && x.name == alt) || x.name == rawName));
  const p = parseChord(tn);
  if (!p) return dbRows;
  const tuning = type == "guitar" ? GUITAR : UKULELE;
  const byKey = new Map<string, { frets: any[]; score: number; row: any }>();
  dbRows.forEach((r) => {
    const f = fromRow(r, type);
    const sc = scoreVoicing(p, f, tuning, tn);
    if (sc !== null) byKey.set(f.join(","), { frets: f, score: sc - 0.2, row: r }); // 같은 점수면 사람이 등록한 것 먼저
  });
  searchVoicings(tn, tuning, 16).forEach((f, i) => {
    const k = f.join(",");
    if (byKey.has(k)) return;
    const sc = scoreVoicing(p, f, tuning, tn);
    if (sc !== null) byKey.set(k, { frets: f, score: sc, row: toRow(tn, f, type, `gen-${type}-${tn}-${i}`) });
  });
  const entries = Array.from(byKey.values());
  const picked = pickDistinct(entries.map((e) => [e.frets, e.score] as [any[], number]), MAX_SHOW[type]);
  return picked.map((f) => byKey.get(f.join(","))!.row);
};

/** 악보에 적힌 코드 이름을 baseKey(반음 수)만큼 옮긴 표시용 HTML (예: "Eᵇ" → "F", "C<sup>#</sup>m") */
export const transposeChord = (chord: string, baseKey: number) => {
  let result = "";
  if (chord) {
    let tempArray = keys;
    chord.match(regex)?.map((chord) => {
      let tempChord = chord;
      if (tempChord === "Bᵇ") {
        tempArray = FUTURE["Bᵇ"];
      } else if (tempChord === "Dᵇ") {
        tempArray = FUTURE["Dᵇ"];
      } else if (tempChord === "Eᵇ") {
        tempArray = FUTURE["Eᵇ"];
      } else if (tempChord === "Gᵇ") {
        tempArray = FUTURE["Gᵇ"];
      } else if (tempChord === "Aᵇ") {
        tempArray = FUTURE["Aᵇ"];
      } else {
        tempArray = keys;
      }
      if (tempArray.indexOf(tempChord) === -1) {
        result = result + tempChord.replace(/♭/g, "ᵇ");
      } else {
        result =
          result +
          tempArray[
            Math.abs(tempArray.indexOf(tempChord) + baseKey) % tempArray.length
          ].replace(/♭/g, "ᵇ");
      }
    });
  }
  return result;
};

export default ({ chord, stopPlay, allChordCode }: { chord: string, stopPlay: () => void, allChordCode: any[] }) => {
  const baseKey = useRecoilValue(baseKeyState);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [listChordData, setListChordData] = useState<any[]>([]);
  const [listChordDataGuitar, setListChordDataGuitar] = useState<any[]>([]);
  const [listChordDataUkelele, setListChordDataUkelele] = useState<any[]>([]);
  const [selectedChordData, setSelectedChordData] = useState<any>(null);
  const [selectedVarient, setSelectedVarient] = useState();
  const [selectedChordType, setSelectedChordType] = useState<string>('guitar');
  const { width: screenWidth = 1980 } = useWindowSize();
  const isSmallScreen = screenWidth <= 768;

  const simple = useRecoilValue(simpleChordState);
  const transposed = transposeChord(chord, baseKey);
  const result = simple && isScoreBeta() ? simplifyChord(transposed) : transposed;

  let firstLoad = true;

  const openChord = async () => {
    if (!result) return;
    track("score:chord");
    firstLoad = true;
    stopPlay();
    setSelectedVarient(undefined);
    setSelectedChordData(null);
    localStorage.setItem('isDisableClickChordLine', 'true');
    setIsModalOpen(true);
    await getChord(selectedChordType);
  }

  const getChord = async (type: string) => {
    const name = result.replace(/<sup>#<\/sup>/g, '#');

    const dataGuitar = buildVoicings(name, 'guitar', allChordCode);
    setListChordDataGuitar(dataGuitar);

    const dataUkelele = buildVoicings(name, 'ukelele', allChordCode);
    setListChordDataUkelele(dataUkelele);

    if (firstLoad) {
      resetChord();
      setSelectedChordType('guitar');
      setListChordData(dataGuitar);

      if (dataGuitar.length > 0) {
        setSelectedVarient((dataGuitar[0] as any).id);
        setSelectedChordData(dataGuitar[0]);
      }
    }
  }

  const onChangeVariant = (id: any) => {
    setSelectedVarient(id);
    setSelectedChordData(listChordData.find((x: any) => x.id == id));
  }

  const isChecked = (line: any, note: any) => {
    if (!selectedChordData) return false;
    if (!selectedChordData[line]) return false;

    const lineJSON = JSON.parse(selectedChordData[line]);
    return lineJSON.includes(Number(note));
  }

  const isHideAllLine = (line: string) => {
    if (!selectedChordData) return true;
    if (!selectedChordData[line]) return true;

    const lineJSON = JSON.parse(selectedChordData['all' + line]);
    // return lineJSON.length == 0;
    return selectedChordData['all' + line] == 0;
  }

  const selectChordType = (type: string) => {
    resetChord();
    setSelectedChordType(type);
    // getChord(type);

    const data = type == 'guitar' ? listChordDataGuitar : listChordDataUkelele;

    setListChordData(data);

    if (data.length > 0) {
      setSelectedVarient((data[0] as any).id);
      setSelectedChordData(data[0]);
    }
  }

  const onPageChange = (page: { selected: number }) => {
    const id = (listChordData[page.selected] as any).id;
    onChangeVariant(id)
  }

  const resetChord = () => {
    setSelectedChordData(null);
  }

  const checkHasCode = () => {
    const name = result.replace(/<sup>#<\/sup>/g, '#');
    // DB 에 있거나, 코드 이름을 해석할 수 있으면(운지법 계산 가능) 누를 수 있게
    if (parseChord(name)) return true;
    const tn = tableName(name);
    return allChordCode.some((x) => x.name == name || x.name == tn);
  }

  const [hiddenCheckAll, setHiddenCheckAll] = useState(false);

  useEffect(() => {
    if (!selectedChordData) {
      setHiddenCheckAll(false);
      return;
    }

    if (selectedChordData['allline1'] == 1 &&
      selectedChordData['allline2'] == 1 &&
      selectedChordData['allline3'] == 1 &&
      selectedChordData['allline4'] == 1 &&
      selectedChordData['allline5'] == 1 &&
      selectedChordData['allline6'] == 1)
      setHiddenCheckAll(true);
    else setHiddenCheckAll(false)

  }, [selectedChordData])

  return (
    <>
      <ChordCodeContainer>
        <ChordCode hasCode={checkHasCode()} beta={isScoreBeta()} long={(result || "").replace(/<[^>]+>/g, "").length} onClick={(e) => { e.stopPropagation(); openChord(); }}
          dangerouslySetInnerHTML={{
            __html: result || "ㅤ<sup>ㅤ</sup>",
          }}
        />
      </ChordCodeContainer>
      <Modal
        width={isSmallScreen ? 280 : 450}
        open={isModalOpen}
        showCloseIcon={true}
        onClose={() => setIsModalOpen(false)}
        content={
          <ChordContainer chordType={selectedChordType} hiddenCheckAll={hiddenCheckAll} onClick={(e) => e.stopPropagation()}>
            <ButtonChordTypeWrap>
              <ButtonChordType active={selectedChordType == 'guitar'} onClick={() => selectChordType('guitar')}>기타</ButtonChordType>
              <ButtonChordType active={selectedChordType == 'ukelele'} onClick={() => selectChordType('ukelele')}>우쿨렐레</ButtonChordType>
            </ButtonChordTypeWrap>
            {/* <div id="variant" className="row">
              {
                listChordData.map((item: any, index) => (
                  <input key={item.id} id={item.id} className="variant" name="variant" type="radio" checked={item.id == selectedVarient} value={item.id}
                    onChange={() => onChangeVariant(item.id)}></input>
                ))
              }
            </div> */}
            {listChordData.length > 1 && (
              <div style={{ textAlign: 'center', fontSize: 12, color: '#9aa0a6', marginTop: 8 }}>
                같은 코드를 잡는 다른 방법 {listChordData.length}가지 (번호를 눌러 보세요)
              </div>
            )}
            <PaginateWrap>
              {listChordData.length > 0 &&
                (
                  <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    breakLabel={'...'}
                    breakClassName={'break-me'}
                    pageCount={listChordData.length}
                    marginPagesDisplayed={1}
                    pageRangeDisplayed={2}
                    onPageChange={onPageChange}
                    containerClassName={'pagination'}
                    activeClassName={'active'}
                    initialPage={0}
                    disableInitialCallback={true}
                  />
                )}
            </PaginateWrap>
            <Row>
              {selectedChordData?.frNumber && (
                <div className="first-col">
                  <div id="frNumberRightSide">{`${selectedChordData?.frNumber}fr`}</div>
                </div>
              )}
              {selectedChordType !== 'ukelele' && (
                <>
                  <div>
                    {!hiddenCheckAll && <div className="fret head">{isHideAllLine('line6') && (<div className="all-sideright-line all-line-hide">x</div>)}</div>}
                    <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line6" checked={isChecked('line6', 1)} readOnly /></div>
                    <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line6" checked={isChecked('line6', 2)} readOnly /></div>
                    <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line6" checked={isChecked('line6', 3)} readOnly /></div>
                    <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line6" checked={isChecked('line6', 4)} readOnly /></div>
                  </div>
                  <div>
                    {!hiddenCheckAll && <div className="fret head">{isHideAllLine('line5') && (<div className="all-sideright-line all-line-hide">x</div>)}</div>}
                    <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line5" checked={isChecked('line5', 1)} readOnly /></div>
                    <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line5" checked={isChecked('line5', 2)} readOnly /></div>
                    <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line5" checked={isChecked('line5', 3)} readOnly /></div>
                    <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line5" checked={isChecked('line5', 4)} readOnly /></div>
                  </div >
                </>
              )
              }
              <div>
                {!hiddenCheckAll && <div className="fret head">{isHideAllLine('line4') && (<div className="all-sideright-line all-line-hide">x</div>)}</div>}
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line4" checked={isChecked('line4', 1)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line4" checked={isChecked('line4', 2)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line4" checked={isChecked('line4', 3)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line4" checked={isChecked('line4', 4)} readOnly /></div>
              </div >
              <div>
                {!hiddenCheckAll && <div className="fret head">{isHideAllLine('line3') && (<div className="all-sideright-line all-line-hide">x</div>)}</div>}
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line3" checked={isChecked('line3', 1)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line3" checked={isChecked('line3', 2)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line3" checked={isChecked('line3', 3)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line3" checked={isChecked('line3', 4)} readOnly /></div>
              </div >
              <div>
                {!hiddenCheckAll && <div className="fret head">{isHideAllLine('line2') && (<div className="all-sideright-line all-line-hide">x</div>)}</div>}
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line2" checked={isChecked('line2', 1)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line2" checked={isChecked('line2', 2)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line2" checked={isChecked('line2', 3)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line2" checked={isChecked('line2', 4)} readOnly /></div>
              </div >
              <div className="last-row">
                {!hiddenCheckAll && <div className="fret head">{isHideAllLine('line1') && (<div className="all-sideright-line all-line-hide">x</div>)}</div>}
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line1" checked={isChecked('line1', 1)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line1" checked={isChecked('line1', 2)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line1" checked={isChecked('line1', 3)} readOnly /></div>
                <div className="fret"><input type="checkbox" className="sideright-checkbox-round sideright-line1" checked={isChecked('line1', 4)} readOnly /></div>
              </div>
            </Row >
            <div className="chordname" dangerouslySetInnerHTML={{
              __html: result || "ㅤ<sup>ㅤ</sup>",
            }}></div>
          </ChordContainer >
        }
      />
    </>
  );
};

const Row = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
`

const Fret = styled.div`
  position: relative;
  border: 1px solid #fff;
  width: 32px;
  height: 64px;

  @media screen and (max-width: 768px) {
    width: 24px;
    height: 48px;
  }
`

const ChordContainer = styled.div<{ chordType: string, hiddenCheckAll: boolean }>`
  display: flex;
  flex-direction: column;
  background-color: black;

  .m-t-20 {
    margin-top: 20px;
  }
  .m-b-10 {
    margin-bottom: 20px;
  }

  .first-col {
    position: absolute;
    left: 60px;

    @media screen and (max-width: 768px) {
      left: 8px;
    }
  }

  .chordname {
    margin-top: 10px;
    margin-bottom: 10px;
    font-size: 24px;
  }

  input[type="checkbox"] {
    outline: none;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-color: transparent;
    border-color: transparent;
  }

  .fret {
    position: relative;
    border: 1px solid white;
    width: 32px;
    height: 64px;

    ${({ chordType }) =>
    chordType == 'ukelele' &&
    css`
      width: 55px;
    `}

    @media screen and (max-width: 768px) {
      width: 24px;
      height: 48px;
      ${({ chordType }) =>
    chordType == 'ukelele' &&
    css`
        width: 40px;
      `}
    }
  }

  .last-row .fret {
    width: 0px;
    border: 1px solid transparent;
  }

  .fret.head {
    /* height: 32px; */
    border: 1px solid transparent;
  }

  #variant {
    margin-top: 10px;
    margin-left: 10px;
  }
  
  .variant {
    margin-right: 15px;
  }

  #frNumberRightSide {
    width: 30px;
    height: 30px;
    left: 30px;
    margin-top: 82px;
    outline: unset;
    margin-right: 20px;
    font-size: 20px;

    ${({ hiddenCheckAll }) =>
    hiddenCheckAll == true &&
    css`
      margin-top: 18px;
      `}

    @media screen and (max-width: 768px) {
      margin-top: 52px;

      ${({ hiddenCheckAll }) =>
    hiddenCheckAll == true &&
    css`
        margin-top: 6px;
        `}
    }
  }

  .all-sideright-line {
    width: 1.8em;
    height: 2em;
    background-color: #212632;
    border-radius: 5px;
    vertical-align: middle;
    outline: none;
    cursor: pointer;
    position: absolute;
    font-weight: bold;
    top: 20px;
    left: -15px;

    @media screen and (max-width: 768px) {
      width: 1.4em;
      height: 1.8em;
      top: 10px;
      left: -11px;
    }
  }

  .all-sideright-line:checked {
    background-color: #0077CC;
    opacity: 1;
  }

  .sideright-checkbox-round {
    width: 1.6em;
    height: 1.6em;
    background-color: #c7dff0;
    border-radius: 50%;
    vertical-align: middle;
    border: 1px dashed #0077CC;
    outline: none;
    cursor: pointer;
    position: absolute;
    top: 16px;
    left: -15px;
    opacity: 0.5;

    @media screen and (max-width: 768px) {
      width: 1.3em;
      height: 1.3em;
      top: 8px;
      left: -10px;
    }
  }

  .sideright-checkbox-round:checked {
    background-color: #0a84ff;
    opacity: 1;
  }
`

const ChordCodeContainer = styled.div`
flex: 1;
width: 25%;
font-size: 13px;
font-weight: 900;
color: #1e1e1e;
// width: 100%;
white-space: pre;
// padding: 3px;
/* white-space: nowrap; */

@media screen and (max-width: 700px) {
  white-space: pre-wrap;
  font-weight: 400;
}

html[color-mode="dark"] & {
  color: #ffffff;
}

.print & {
  color: #1e1e1e !important;
}

@media screen and (max-width: 1300px) {
  font-size: 13px;
}

@media screen and (max-width: 1075px) {
  font-size: 12px;
}

@media screen and (max-width: 1040px) {
  font-size: 11px;
}

@media screen and (max-width: 685px) {
  font-size: 11px;
}

@media screen and (max-width: 600px) {
  font-size: 8px;
}

@media screen and (max-width: 585px) {
  transform: scale(0.7);
  transform-origin: left;
}

@media screen and (max-width: 575px) {
  transform: scale(0.55);
}

/* 휴대폰 가로 보기: 폭이 넓어 줄이지 않던 코드 칩이 가사보다 훨씬 커 보였다(2026-10-11 사장님) */
@media screen and (orientation: landscape) and (max-height: 500px) {
  transform: scale(0.72);
  transform-origin: left;
}
`;

const ChordCode = styled.var<{ hasCode: boolean; beta?: boolean; long?: number }>`
  ${({ hasCode, beta }) =>
    hasCode && !beta &&
    css`
    border: 2px solid #0077CC;
    border-radius: 5px;
    padding: 2px;
  `}
  ${({ hasCode, beta }) =>
    hasCode && beta &&
    css`
    /* 누르면 운지법이 나오는 코드: 호박색 칩 (2026-10 새 디자인) */
    border: 2px solid #3a3220;
    border-radius: 6px;
    padding: 2px 4px;
    background: #2a2416;
    color: #ffb547;
    font-style: normal;
  `}
  ${({ hasCode, beta, long = 0 }) =>
    hasCode && beta && long >= 5 &&
    css`
    /* 긴 코드는 한 박 칸 안에 들어가게 글자를 줄임 */
    font-size: ${long >= 8 ? 0.66 : long >= 6 ? 0.74 : 0.84}em;
    letter-spacing: -0.03em;
    padding: 2px 2px;
  `}
`;

const ButtonChordTypeWrap = styled.div`
  display: flex;
  padding: 15px;
  justify-content: center;
  gap: 10px;
`;

const ButtonChordType = styled.button<{ active: boolean }>`
  color: white;
  background-color: #cccccc;
  border: none;
  width: 120px;
  border-radius: 5px;

  ${({ active }) =>
    active &&
    css`
    background-color: #0077CC;
  `}
`;

const PaginateWrap = styled.div`
  margin-top: 10px;

  .pagination {
    margin: 0;
    a {
      background-color: transparent;
      color: white;
      border: none;
      &:focus {
        background-color: transparent;
        border: none;
        outline: none;
        box-shadow: none;
      }
    }
    .active {
      a{
        color: #0077CC;
      }
    }

    @media screen and (max-width: 768px) {
      a {
        padding: 0px;
        width: 25px;
      }
    }
  }
`;